/**
 * email-signup D5 — the email signup box, behavior half. Holds the ticket in page memory only: no browser
 * storage of any kind. Draws nothing.
 *
 * - The ticket is read on the field's first focus, or by Subscribe when nothing focused the field (autofill).
 * - A ticket is held until it is old enough: Subscribe pressed at once waits, then goes once. Only a refused
 *   ticket is read again, twice at most, and the fresh one is held the same way, so it never loops.
 * - A refused address or a limit keeps the box with what was typed. A limit used the ticket up, so the next
 *   Subscribe reads a fresh one.
 */
import { useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { LEGAL_PAGE_PATHS } from '@telify/shared/browser';

import { sitePost } from '../../api/session';
import { SiteFetchError, useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { contract, type EmailSignupActions, type EmailSignupEvents, type EmailSignupState } from './contract';

export type EmailSignupInput = PieceInput<EmailSignupEvents>;

export type EmailSignupRuntime = PieceRuntime<typeof contract, EmailSignupState, EmailSignupActions> & {
  /** The consent line's words, with the studio's name in place; they are stored with the signup. */
  readonly consent: string;
  readonly privacyHref: string;
  /** Reads the ticket ahead of Subscribe; the field calls it on focus. */
  readonly prepare: () => void;
  /** The off-screen trap field: a person never fills it. */
  readonly trapRef: RefObject<HTMLInputElement>;
};

interface Ticket {
  readonly ticket: string;
  /** When the ticket was read here: the hold counts from then, never earlier than the server issued it. */
  readonly readAt: number;
}

/** The server refuses a ticket younger than 2 seconds; the hold adds a margin. */
const TICKET_HOLD_MS = 2_100;
const ENTER_EMAIL = 'Enter an email address.';
const TRY_LATER = 'Please try again later.';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useEmailSignup(input: EmailSignupInput): EmailSignupRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const studioName = useSiteStudio().data?.name ?? 'the studio';

  const [status, setStatus] = useState<EmailSignupState['status']>('form');
  const [error, setError] = useState<string | null>(null);

  const ticket = useRef<Promise<Ticket> | null>(null);
  const sending = useRef(false);
  const trapRef = useRef<HTMLInputElement>(null);

  const consent = (options.consent ?? contract.options.consent.default).split('{studio}').join(studioName);

  /** The ticket held, or a fresh one; a read that failed is forgotten so the next asks again. */
  const held = useCallback(() => {
    ticket.current ??= sitePost<{ ticket: string }>('/email-list/ticket').then(
      (r) => ({ ticket: r.ticket, readAt: Date.now() }),
      (err: unknown) => {
        ticket.current = null;
        throw err;
      },
    );
    return ticket.current;
  }, []);

  const prepare = useCallback(() => void held().catch(() => {}), [held]);

  const submit = useCallback(
    async (raw: string) => {
      if (sending.current) return;
      const email = raw.trim();
      if (!EMAIL.test(email)) return setError(ENTER_EMAIL);
      sending.current = true;
      setError(null);
      setStatus('sending');
      const trap = trapRef.current?.value;
      try {
        for (let reads = 0; ; reads += 1) {
          const t = await held();
          await wait(Math.max(0, t.readAt + TICKET_HOLD_MS - Date.now()));
          try {
            await sitePost('/email-list/signup', {
              email,
              ticket: t.ticket,
              page: window.location.pathname,
              consentText: consent,
              ...(trap ? { website: trap } : {}),
            });
            ticket.current = null;
            setStatus('done');
            emit('subscribed', {});
            return;
          } catch (err) {
            const code = err instanceof SiteFetchError ? err.code : undefined;
            if (code === 'VALIDATION_FAILED') throw new Refusal(ENTER_EMAIL);
            // A limit is counted after the ticket is used, and anything else may have used it too.
            ticket.current = null;
            if (code === 'TICKET_INVALID' && reads < 2) continue;
            throw new Refusal(TRY_LATER);
          }
        }
      } catch (err) {
        setError(err instanceof Refusal ? err.line : TRY_LATER);
        setStatus('form');
      } finally {
        sending.current = false;
      }
    },
    [held, consent, emit],
  );

  const actions = useMemo<EmailSignupActions>(() => ({ submit: (email) => void submit(email) }), [submit]);

  return {
    state: { status, error },
    actions,
    options,
    consent,
    privacyHref: LEGAL_PAGE_PATHS.privacyPolicy,
    prepare,
    trapRef,
  };
}

/** A signup refused with a line to show. */
class Refusal extends Error {
  constructor(readonly line: string) {
    super(line);
  }
}
